import _debounce from "lodash/debounce";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { DELIVERY_TYPES_WITH_CODE, DeliveryType, API_URL_SET_DELIVERY_TYPE } from "../../../../../constants";
import { DeliveryMethod, RootState, OfferingCode, ICartItem } from "../../../../../models";
import { get as Ajax_get, post as Ajax_post } from "../../../app/Ajax";
import { API_URL_FETCH_DELIVERY_AND_PAYMENT_ALL, API_URL_GET_INSURANCE_INFO } from "../../api";
import * as AT from "../actionTypes";
import { getCartContent } from "../selectors/cartSelectors";
import { getDeliveryInfoMethods, getDeliveryInfoType } from "../selectors/deliveryInfoSelectors";

export const fetchDeliveryAndPaymentMethods = (): ThunkAction<void, RootState, unknown, AnyAction> => (dispatch) =>
    dispatch({
        type: AT.CHECKOUT_FETCH_DELIVERY_AND_PAYMENT_METHODS,
        payload: Ajax_get(
            {
                url: API_URL_FETCH_DELIVERY_AND_PAYMENT_ALL,
            },
            dispatch,
        ),
    });

// We need the debounce version because this function will possibly be triggered twice on changing delivery method.
// 1) 'setSelectedDeliveryType' will trigger method if 'deliveryType' has changed.
// 2) 'Summary.jsx => hook' will trigger method if 'totalPaymentPrice' has changed.
// When changing to a new delivery method with a different price, it will cause this method to be called from both scenarios above.
// Thus, a small debounce time is added to only accept the latter of these calls.
const innerFunction = _debounce((dispatch) => dispatch(fetchDeliveryAndPaymentMethods()), 200);
export const debouncedFetchDeliveryAndPaymentMethods = () => innerFunction;

export const setSelectedDeliveryType =
    ({
        deliveryType,
        code,
        zipCode,
    }: {
        code?: string | null;
        deliveryType: DeliveryType;
        zipCode?: string | number;
    }): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        let promise;
        let shouldUpdateDeliveryAndPaymentMethods = false;

        // Can't update PICK_UP_IN_SHOP, PICK_UP_POINT before a code is chosen
        if ((DELIVERY_TYPES_WITH_CODE as readonly string[]).includes(deliveryType) && code === null) {
            promise = Promise.resolve({ cart: { content: getCartContent(getState()) } });
        } else {
            promise = Ajax_post<{ cart: { content: ICartItem[] } }>(
                {
                    url: API_URL_SET_DELIVERY_TYPE,
                    data: {
                        type: deliveryType,
                        code,
                        zipCode,
                    },
                },
                dispatch,
            );

            // If delivery method changes we need to update payment methods
            const oldDeliveryType = getDeliveryInfoType(getState());
            if (oldDeliveryType !== deliveryType) {
                shouldUpdateDeliveryAndPaymentMethods = true;
            }
        }

        if (shouldUpdateDeliveryAndPaymentMethods) {
            promise.then(() => {
                dispatch(debouncedFetchDeliveryAndPaymentMethods());
            });
        }

        const selectedDeliveryInfo = getDeliveryInfoMethods(getState()).find(
            (deliveryMethod: DeliveryMethod) => deliveryMethod.type === deliveryType,
        );

        return dispatch({
            type: AT.CHECKOUT_SET_DELIVERY_TYPE,
            payload: promise.then((data) => ({
                deliveryType,
                code,
                price: selectedDeliveryInfo?.price,
                name: selectedDeliveryInfo?.label,
                description: selectedDeliveryInfo?.description,
                cart: data.cart,
            })),
        });
    };

export const getInsuranceInfo =
    (sku: string, offeringCode: OfferingCode): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) =>
        dispatch({
            type: AT.CHECKOUT_GET_INSURANCE_INFO,
            payload: Ajax_get(
                {
                    url: API_URL_GET_INSURANCE_INFO(sku, offeringCode),
                },
                dispatch,
            ),
        });
